import React, { useState, useMemo } from 'react';
import { Droplets } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, SelectField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBaseWaterMl, calculateWorkoutWaterMl, ACTIVITY_FACTORS } from '../../lib/calculatorUtils';

const activityOptions = Object.entries(ACTIVITY_FACTORS).map(([k, v]) => ({
  value: k, label: `${v.label}: ${v.desc}`
}));

const WaterIntakeCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [weightKg, setWeightKg] = useState('');
  const [activity, setActivity] = useState('moderate');
  const [workoutHours, setWorkoutHours] = useState('');

  const result = useMemo(() => {
    const w = parseFloat(weightKg);
    if (!w || w < 20) return null;
    const baseMl = calculateBaseWaterMl(w);
    const workoutH = parseFloat(workoutHours) || 0;
    const workoutMl = calculateWorkoutWaterMl(workoutH, activity);
    const totalMl = baseMl + workoutMl;
    return {
      baseMl: Math.round(baseMl),
      workoutMl: Math.round(workoutMl),
      totalMl: Math.round(totalMl),
      liters: +(totalMl / 1000).toFixed(2),
      glasses: Math.round(totalMl / 250),
      oz: Math.round(totalMl / 29.5735)
    };
  }, [weightKg, activity, workoutHours]);

  const inputs = (
    <div className="space-y-5" data-testid="water-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Hydration Inputs</h2>
      <p className="text-slate-400 text-sm mb-5">We adjust your target based on activity level &amp; training time.</p>
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <SelectField id="water-activity" label="Activity Level" value={activity} onChange={setActivity} options={activityOptions} testId="water-activity-select" required />
      <NumberField id="water-workout" label="Workout Duration (today)" value={workoutHours} onChange={setWorkoutHours} placeholder="e.g. 1" suffix="hours" min={0} max={8} testId="water-workout-input" />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Daily Water Target" value={result.liters} unit="L" accent="from-cyan-500 to-blue-500" testId="water-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Total (ml)" value={result.totalMl.toLocaleString()} unit="ml" testId="water-total-ml" />
        <StatRow label="Approx. glasses (250 ml)" value={result.glasses} testId="water-glasses" />
        <StatRow label="Approx. ounces" value={result.oz} unit="oz" testId="water-oz" />
        <StatRow label="Baseline" value={result.baseMl.toLocaleString()} unit="ml" testId="water-baseline" />
        <StatRow label="Workout hydration" value={result.workoutMl.toLocaleString()} unit="ml" testId="water-workout-ml" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Adjust based on climate, illness, pregnancy or breastfeeding. Consult a clinician for medical conditions affecting fluid balance.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your weight and activity to see your daily hydration target." />
  );

  return (
    <CalculatorLayout
      slug="water-intake"
      title="Daily Water Intake Calculator"
      subtitle="Find out how much water you should drink daily based on your weight, activity level, and workout duration."
      metaDescription="Calculate your daily water intake with the TCUBE FITNESS Water Calculator, adjusted for weight, activity, and workout time."
      icon={Droplets}
      accentGradient="from-cyan-500 to-blue-500"
      accentColor="text-cyan-400"
      badgeBg="bg-cyan-500/10"
      badgeBorder="border-cyan-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">Formula:</strong> Base Water = weight (kg) × 35 ml</p>
          <p>Add 500–1000 ml per hour of exercise, scaled by activity intensity.</p>
          <p><em>Example:</em> 70 kg × 35 = 2,450 ml/day + workout hydration.</p>
        </>
      }
    />
  );
};

export default WaterIntakeCalculatorPage;
